import React, { useState, useEffect } from 'react';
import { motion, useMotionValue, animate } from 'motion/react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { db } from '../../firebase';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';

const staticTestimonials = [
  {
    id: '1',
    name: "Chinelle Ellington",
    company: "RE/MAX METROPOLIS",
    quote: "Working with Realta Media has been exceptional. Not your average videography service - they bring professionalism and cutting-edge tech that set a new standard. Our last video hit 30,000 views thanks to their intentional creativity, quality, and storytelling. Dixit's patience, passion, and meticulous attention to detail show up in a clear vision and lightning-fast turnaround. Realta Media is the one to call, visually stunning work built on professionalism and purpose, - highly recommend."
  },
  {
    id: '2',
    name: "Hussein Kabani",
    company: "RE/MAX ROUGE RIVER",
    quote: "We've been working with Realta Media since early 2022, and we haven't looked back. Dixit and his team deliver high-quality media that helps our listings stand out. What sets them apart is how collaborative they are - they guide us on how to present the property. Their process helps us create content that's visually stunning and strategically designed to capture the attention of potential buyers. Realta Media is a key part of our marketing success."
  },
  {
    id: '3',
    name: "Alex Giancoulas",
    company: "REVEL REALTY",
    quote: "Had a great experience. Went over and above and the quality of the work was exceptional. Offered guidance and suggestions and takes the time to make sure your listing shows amazing. Highly recommended."
  },
  {
    id: '4',
    name: "Kim Chung",
    company: "FUTURE GROUP",
    quote: "The quality of the photos is on another level! The composition of the photos and the attention to detail is incredible. I am thrilled with the media that Dixit shot for my listings (drone photos, 3D tour, floor plans and more!). He took the time to understand what I wanted and produced photos that aligned with my goals. He was able to fit me in last minute and he works hard to provide quality service. Thank you for being so amazing - it is appreciated. I highly recommend Realta Media. You will not be disappointed!"
  },
  {
    id: '5',
    name: "Amanda McCaskie",
    company: "RE/MAX HALLMARK",
    quote: "Quick turnaround, high-quality photos and awesome video skills. I got lots of compliments on the video and reel that he made for my client's house. I will continue to use Realta Media in the future and would highly recommend them to anyone looking for Real estate Media."
  },
  {
    id: '6',
    name: "Jack Sidhu",
    company: "SIDHU REALTY",
    quote: "Hired Realta Media for my listing and I was extremely satisfied with the service and quality. Dixit was very thorough with his work, patient and professional, he took the time to explain details before the shoot. I will definitely work with him again for my future listings therefore I highly recommend Realta Media!"
  }
];

export const Testimonials: React.FC = () => {
  const [testimonials, setTestimonials] = useState<any[]>(staticTestimonials);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const q = query(collection(db, 'testimonials'), orderBy('order', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTestimonials(data);
      } else {
        setTestimonials(staticTestimonials);
      }
      setLoading(false);
    }, (error) => {
      console.error("Error fetching testimonials:", error);
      setTestimonials(staticTestimonials);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const START_INDEX = testimonials.length; 
  const extendedTestimonials = [...testimonials, ...testimonials, ...testimonials, ...testimonials];
  const [currentIndex, setCurrentIndex] = useState(START_INDEX);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isHovered, setIsHovered] = useState(false);
  
  // Motion value for x position to allow drag + programmatic control
  const x = useMotionValue(0);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate card width + gap based on screen size
  const getCardWidth = () => (isMobile ? window.innerWidth * 0.85 : 500);
  const gap = 24;
  const slideWidth = getCardWidth() + gap;

  // Auto-scroll
  useEffect(() => {
    if (isHovered || testimonials.length === 0) return;
    const interval = setInterval(() => { handleNext(); }, 4000); // Slower auto-scroll
    return () => clearInterval(interval);
  }, [currentIndex, isHovered, isTransitioning, testimonials.length]);

  // Sync motion value when index changes
  useEffect(() => {
    if (testimonials.length === 0) return;
    const newX = -(currentIndex * slideWidth);
    
    if (isTransitioning) {
       animate(x, newX, {
          type: "spring",
          stiffness: 300,
          damping: 30,
          onComplete: handleTransitionEnd
       });
    } else {
       x.set(newX);
    }
  }, [currentIndex, slideWidth, testimonials.length]);

  const handleNext = () => {
    if (isTransitioning || testimonials.length === 0) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => prev + 1);
  };

  const handlePrev = () => {
    if (isTransitioning || testimonials.length === 0) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => prev - 1);
  };

  const handleTransitionEnd = () => {
    setIsTransitioning(false);
    const totalOriginal = testimonials.length;
    if (totalOriginal === 0) return;
    
    // Instant reset without animation when reaching the bounds
    if (currentIndex >= START_INDEX + totalOriginal) {
      setCurrentIndex(currentIndex - totalOriginal);
      x.set(-(currentIndex - totalOriginal) * slideWidth);
    } else if (currentIndex < START_INDEX) {
      setCurrentIndex(currentIndex + totalOriginal);
      x.set(-(currentIndex + totalOriginal) * slideWidth);
    }
  };

  if (loading && testimonials.length === 0) return null;

  return (
    <section className="py-14 md:py-24 bg-black border-t border-white/5">
        <div className="w-full overflow-hidden">
           <div className="px-6 md:px-12 mb-12 flex flex-col items-start gap-4">
              <div>
                 <span className="text-brand-500 font-bold uppercase tracking-[0.2em] text-[10px]">Testimonials</span>
                 <h2 className="text-4xl md:text-5xl font-display font-bold uppercase mt-4">Client <span className="text-zinc-600">Love</span></h2>
              </div>
           </div>

           <div className="relative w-full pl-10 md:pl-12 mb-12"
                onMouseEnter={() => setIsHovered(true)} 
                onMouseLeave={() => setIsHovered(false)}
           >
              <motion.div 
                 className="flex gap-6 cursor-grab active:cursor-grabbing"
                 style={{ x, touchAction: "pan-y" }} // pan-y allows vertical scrolling while touching
                 drag="x"
                 dragConstraints={{ left: -((extendedTestimonials.length - 1) * slideWidth), right: 0 }}
                 dragElastic={0.1} // More resistance
                 onDragEnd={(e, { offset, velocity }) => {
                   const swipe = offset.x;
                   if (swipe < -50 || velocity.x < -500) {
                     handleNext();
                   } else if (swipe > 50 || velocity.x > 500) {
                     handlePrev();
                   } else {
                     // Snap back if not enough swipe
                     animate(x, -(currentIndex * slideWidth), { type: "spring", stiffness: 300, damping: 30 });
                   }
                 }}
              >
                 {extendedTestimonials.map((t, i) => (
                    <div 
                      key={`${t.id}-${i}`}
                      className="w-[85vw] md:w-[500px] bg-[#111] border border-white/5 p-8 md:p-12 flex flex-col justify-between h-[600px] md:h-[660px] flex-shrink-0 select-none"
                    >
                        <div className="flex-grow overflow-hidden flex flex-col">
                           <div className="flex gap-1 mb-6 flex-shrink-0">
                              {[1,2,3,4,5].map(s => <span key={s} className="text-brand-500 text-base">★</span>)}
                           </div>
                           <p className="text-white/90 text-[13.25px] md:text-xl font-light leading-relaxed overflow-y-auto scrollbar-hide pr-2">"{t.quote}"</p>
                        </div>
                        
                        <div className="mt-8 pt-8 border-t border-white/5 flex-shrink-0">
                           <h4 className="text-white font-bold text-[19px] md:text-2xl">{t.name}</h4>
                           <p className="text-zinc-500 text-xs md:text-sm uppercase tracking-wide mt-1">{t.company}</p>
                        </div>
                    </div>
                 ))}
              </motion.div>
           </div>
           
           <div className="px-6 md:px-12 flex gap-6">
                <button 
                  onClick={handlePrev} 
                  className="w-20 h-20 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all group"
                  aria-label="Previous testimonial"
                >
                    <ArrowLeft size={32} className="group-hover:-translate-x-1 transition-transform" />
                </button>
                <button 
                  onClick={handleNext} 
                  className="w-20 h-20 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all group"
                  aria-label="Next testimonial"
                >
                    <ArrowRight size={32} className="group-hover:translate-x-1 transition-transform" />
                </button>
           </div>
        </div>
      </section>
  );
};